import Link from 'next/link';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { useCheckoutMode, CheckoutModes } from '@/hooks/useCheckoutMode';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { ModalBadge } from '@/ui/Icons/ModalBadge';

import { classNames } from '@/utils/classnames';
import { routes } from '@/utils/routes';

import { AlertSuccessGradient } from '../../Alerts/shared';

const MOVE_DOWN = 'animate-[moveDown_500ms_cubic-bezier(0.4,0,0,1)_forwards]';
const FADE_IN_AND_GROW = 'animate-[fadeInAndGrowAmount_500ms_cubic-bezier(0.4,0,0,1)_forwards]';
const FADE_IN = 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]';

/** Renders a submitted limit order with caller-owned checkout reset. */
export const SubmittedOrderView = ({ onReset }: { onReset: () => void }) => {
  const [, updateCheckoutMode] = useCheckoutMode();
  const [orderType] = useAdvancedOrderType();

  const handleNewTrade = () => {
    onReset();
    updateCheckoutMode(CheckoutModes.advanced);
  };

  return (
    <div className="relative flex flex-col justify-between size-full gap-10 overflow-hidden min-h-full">
      <div className="pt-10 px-6">
        <div className="text-center mb-auto tablet:mb-[72px] desktop:mb-auto">
          <AlertSuccessGradient />
          <ModalBadge
            variant="success"
            className={classNames('relative mx-auto mb-7', MOVE_DOWN)}
          />
          <div className={fontStyles({ variant: 'H5', class: ['mb-2'] })}>
            {ADVANCED_ORDER_TYPE_LABELS[orderType]} order placed!
          </div>
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['mt-4 text-token-gray-800 opacity-0 max-h-0 tablet:hidden', FADE_IN_AND_GROW],
            })}
          >
            You can see more details in{' '}
            <Link
              className={linkStyles({ underline: true, color: 'purple' })}
              href={{
                pathname: routes.TRADE_HISTORY,
                query: { tab: 'open-orders' },
              }}
            >
              Trade History
            </Link>
          </div>
        </div>
      </div>

      <div className="flex w-full gap-2.5 pt-4 desktop:pt-0 pb-4 desktop:pb-6 px-2 desktop:px-6">
        <button
          className={buttons({
            size: 'large',
            color: 'black',
            shape: 'pill',
            class: FADE_IN,
          })}
          onClick={handleNewTrade}
        >
          New Trade
        </button>
      </div>
    </div>
  );
};
